import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Landmark, Sparkles, Fingerprint, Lock, ScrollText } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import FloatingEmblem from "@/components/FloatingEmblem";
import HeroVideo from "@/components/HeroVideo";
import { NODE_SPHERE_3D } from "@/lib/api";

const IMG = {
  vault: "https://images.pexels.com/photos/25913209/pexels-photo-25913209.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  heritage: "https://images.pexels.com/photos/12397217/pexels-photo-12397217.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const pillars = [
  { icon: ScrollText, title: "Preservation", tag: "Cultural & Historical", text: "Immutable safekeeping of genealogies, oral histories, and ancestral archives — permanently anchored beyond the reach of external revision." },
  { icon: Landmark, title: "Stewardship", tag: "Lineage & Assets", text: "Multigenerational management of lineage records and shared assets, governed by the family rather than federal or state trust constraints." },
  { icon: Sparkles, title: "Prosperity", tag: "Economic Sovereignty", text: "Digital economic citizenship, mutual-aid networks, and independent instruments that convert verified heritage into durable autonomy." },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-ink" />
        <HeroVideo />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.10)_0%,transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <Eyebrow>The Gateway to Freedom</Eyebrow>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                data-testid="home-hero-title"
                className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6"
              >
                Stewardship Across
                <br />
                <span className="text-gold-gradient">7 Generations.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 }}
                className="font-sans text-base sm:text-lg text-[#A19D98] leading-relaxed mt-8 max-w-xl"
              >
                The private institutional framework for unrepresented Woodland Mississippian
                descendants and Modern Freedmen — securing our heritage through decentralized
                sovereignty.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 mt-12"
              >
                <Link
                  to="/eligibility?tab=verify"
                  data-testid="hero-verify-btn"
                  className="group flex items-center justify-center gap-3 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors duration-300"
                >
                  Verify Genealogy
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-vault-btn"
                  className="flex items-center justify-center gap-3 px-8 py-4 border border-gold/40 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:border-gold hover:bg-gold/5 transition-colors duration-300"
                >
                  <Lock size={14} /> Enter the Vault
                </Link>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.6 }}
                className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/50 mt-12"
              >
                Preservation • Stewardship • Prosperity
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 relative"
            >
              <FloatingEmblem />
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 gold-hairline" />
      </section>

      {/* PILLARS */}
      <section className="py-28 sm:py-32 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Institutional Philosophy</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-5 max-w-2xl">
              Three pillars carried forward through every generation.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px mt-16 border border-gold/15 bg-gold/10">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12} className="bg-ink">
                <div className="p-10 h-full group hover:bg-ink-surface transition-colors duration-500">
                  <p.icon size={28} className="text-gold" strokeWidth={1.25} />
                  <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold/60 mt-8">{p.tag}</p>
                  <h3 className="font-serif text-3xl mt-2">{p.title}</h3>
                  <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-5">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCKCHAIN REGISTRY */}
      <section className="py-28 sm:py-32 bg-ink-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="border border-gold/20 overflow-hidden bg-ink relative">
              <motion.img
                src={NODE_SPHERE_3D}
                alt="Decentralized cryptographic node sphere"
                className="w-full h-[440px] object-cover"
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-ink border border-gold/30 px-6 py-4 hidden sm:block">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold/60">Block Anchored</p>
              <p className="font-mono text-sm text-gold mt-1">SHA-256 · Immutable</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow><Fingerprint size={12} className="inline mr-2 -mt-0.5" />Decentralized Blockchain Ancestry Registry</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-5">
              Records that cannot be erased, revised, or reclaimed.
            </h2>
            <p className="font-sans text-base text-[#A19D98] leading-relaxed mt-6">
              The Trust utilizes the most modern decentralized blockchain architecture to timestamp,
              encrypt, and permanently store genealogical records. Your family trees and ancestral
              archives remain private, unalterable, and entirely controlled by the lineage — never by
              external government bodies.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Cryptographic timestamping of every uploaded record",
                "Family-controlled data sovereignty, end to end",
                "Independent of federal or state tribal frameworks",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <ShieldCheck size={18} className="text-gold mt-0.5 shrink-0" strokeWidth={1.4} />
                  <span className="font-sans text-sm text-[#C9C4BD]">{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/eligibility?tab=verify"
              data-testid="registry-cta"
              className="group inline-flex items-center gap-3 mt-10 font-mono text-[11px] tracking-[0.2em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors"
            >
              Begin verification <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FREEDMEN BUREAU CTA */}
      <section className="relative py-32 bg-ink overflow-hidden">
        <img src={IMG.heritage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-ink/80" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <Eyebrow className="justify-center">The Modern Freedmen Bureau</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-6xl tracking-tight mt-6 leading-tight">
              An independent registry and socio-economic anchor for descendants.
            </h2>
            <p className="font-sans text-base text-[#A19D98] leading-relaxed mt-8 max-w-2xl mx-auto">
              For Urban Indians with confirmed Woodland Mississippian ancestry and Modern Freedmen
              lineages who have completed their genealogy — yet fall outside federal or state tribal
              enrollment — the Bureau provides recognition, permanence, and independent economic sovereignty.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Link to="/eligibility" data-testid="cta-eligibility" className="px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
                Eligibility & Lineage
              </Link>
              <Link to="/mission" data-testid="cta-mission" className="px-8 py-4 border border-gold/40 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold/5 transition-colors">
                Our Mission
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
